import { useCallback, useState } from "react";
import type { ImageSourcePropType, LayoutChangeEvent } from "react-native";
import { AnimatePresence, Image, isWeb } from "tamagui";
import { FormInput } from "./bento";
import { StyledSelectButton } from "./GeneralStyledComponents";
import { LoadingButton } from "./LoadingButton";
import { XStack, YStack } from "./Stacks";
import { Headline, Paragraph } from "./Typography";
export type FeedbackFormOptionConfig = {
  label: string;
  image?: string | number;
  emoji?: string;
};
const getImageSource = (image: FeedbackFormOptionConfig["image"]): {
  src?: string;
} | {
  source?: ImageSourcePropType;
} => {
  if (!image) {
    return {};
  }
  // Native images with require return a number, web images will be strings
  return isWeb ? {
    src: image as string
  } : {
    source: image as number
  };
};
export type MoreDetailsProps = {
  onSubmit: (selectedOptions: string[], moreDetailsText: string) => void | Promise<void>;
  triggerLabels: string[];
  title?: string;
  placeholder?: string;
  submitText?: string;
};
type FeedbackFormProps = {
  title: string;
  subtitle?: string;
  options: FeedbackFormOptionConfig[];
  continueButtonText?: string;
  loading?: boolean;
  onContinue: (selectedOptions: string[]) => void;
  moreDetailsProps?: MoreDetailsProps;
};
export function FeedbackForm({
  title,
  subtitle,
  options,
  continueButtonText = "Continue",
  loading = false,
  onContinue,
  moreDetailsProps
}: FeedbackFormProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showMoreDetailsForm, setShowMoreDetailsForm] = useState(false);
  const [storedSelectedOptions, setStoredSelectedOptions] = useState<string[]>([]);
  const [moreDetailsText, setMoreDetailsText] = useState("");
  const toggleOption = useCallback((label: string) => {
    setSelectedOptions(prev => prev.includes(label) ? prev.filter(o => o !== label) : [...prev, label]);
  }, []);
  const handleContinue = useCallback(() => {
    const hasMoreDetailsLabel = moreDetailsProps ? selectedOptions.some(option => moreDetailsProps.triggerLabels.includes(option)) : false;
    if (hasMoreDetailsLabel && moreDetailsProps) {
      // Store selected options and show more details form
      setStoredSelectedOptions(selectedOptions);
      setMoreDetailsText("");
      setShowMoreDetailsForm(true);
    } else {
      // Call onContinue immediately (either no trigger label selected or more details not supported)
      onContinue(selectedOptions);
    }
  }, [selectedOptions, onContinue, moreDetailsProps]);
  const handleMoreDetailsSubmit = useCallback(async () => {
    if (moreDetailsProps) {
      await moreDetailsProps.onSubmit(storedSelectedOptions, moreDetailsText);
    }
  }, [moreDetailsProps, storedSelectedOptions, moreDetailsText]);
  const shouldShowMoreDetails = showMoreDetailsForm && moreDetailsProps;
  const [initialFormHeight, setInitialFormHeight] = useState<number | null>(null);
  const [moreDetailsFormHeight, setMoreDetailsFormHeight] = useState<number | null>(null);
  const [buttonAreaHeight, setButtonAreaHeight] = useState<number | null>(null);
  const handleInitialFormLayout = useCallback((event: LayoutChangeEvent) => {
    const {
      height
    } = event.nativeEvent.layout;
    setInitialFormHeight(height);
  }, []);
  const handleMoreDetailsFormLayout = useCallback((event: LayoutChangeEvent) => {
    const {
      height
    } = event.nativeEvent.layout;
    setMoreDetailsFormHeight(height);
  }, []);
  const handleButtonAreaLayout = useCallback((event: LayoutChangeEvent) => {
    if (buttonAreaHeight !== null) {
      return;
    }
    const {
      height
    } = event.nativeEvent.layout;
    setButtonAreaHeight(height);
  }, [buttonAreaHeight]);

  // Determine height to animate to
  const containerHeight = shouldShowMoreDetails ? (moreDetailsFormHeight ?? 0) + (buttonAreaHeight ?? 0) : (initialFormHeight ?? 0) + (buttonAreaHeight ?? 0);
  const submitButtonProps = shouldShowMoreDetails ? {
    disabled: false,
    onPress: handleMoreDetailsSubmit
  } : {
    disabled: selectedOptions.length === 0 || loading,
    onPress: handleContinue
  };
  const initialForm = <YStack key="initial" flex={shouldShowMoreDetails ? undefined : 1} flexShrink={0} gap="$6" position={shouldShowMoreDetails ? "absolute" : "relative"} left={shouldShowMoreDetails ? 0 : undefined} right={shouldShowMoreDetails ? 0 : undefined} top={shouldShowMoreDetails ? 0 : undefined} width="100%" onLayout={handleInitialFormLayout} exitStyle={{
    opacity: 0
  }} animation="200ms easeOut">
      <YStack gap="$3.5">
        <Headline ta="center">{title}</Headline>
        {subtitle ? <Paragraph ta="center" theme="alt1">
            {subtitle}
          </Paragraph> : null}
      </YStack>

      <YStack gap="$3.5" flex={shouldShowMoreDetails ? undefined : 1} flexShrink={0} alignItems="center" justifyContent="center">
        {options.map(option => {
        const {
          label,
          image,
          emoji
        } = option;
        const imageProps = getImageSource(image);
        return <StyledSelectButton key={label}
        // Self sizing buttons
        flexGrow={0} flexShrink={0} alignSelf="center" selected={selectedOptions.includes(label)} onPress={() => toggleOption(label)}>
              {emoji ? <XStack gap="$2" alignItems="center">
                  <Paragraph>{emoji}</Paragraph>
                  <Paragraph color="$navy12">{label}</Paragraph>
                </XStack> : image ? <XStack gap="$2" alignItems="center">
                  <Image {...imageProps} width={24} height={24} borderRadius={4} />
                  <Paragraph color="$navy12">{label}</Paragraph>
                </XStack> : label}
            </StyledSelectButton>;
      })}
      </YStack>
    </YStack>;
  const moreDetailsForm = <YStack key="more-details" flex={1} gap="$6" onLayout={handleMoreDetailsFormLayout} position="absolute" left={0} right={0} top={0} width="100%" enterStyle={{
    opacity: 0
  }} animation="200ms easeOut">
      <YStack gap="$3.5">
        <Headline ta="center">
          {moreDetailsProps?.title ?? "Any more details? Your feedback helps improve Granted!"}
        </Headline>
      </YStack>

      <FormInput value={moreDetailsText} onChangeText={setMoreDetailsText} placeholder={moreDetailsProps?.placeholder} multiline numberOfLines={4} autoFocus />
    </YStack>;
  return <YStack flex={1} position="relative" height={isWeb ? containerHeight ?? undefined : undefined} minHeight={!isWeb ? containerHeight ?? undefined : undefined} animation={containerHeight !== null ? "200ms easeOut" : undefined} overflow={isWeb ? "hidden" : undefined} data-sentry-element="YStack" data-sentry-component="FeedbackForm" data-sentry-source-file="FeedbackForm.tsx">
      <YStack flex={1} position="relative" data-sentry-element="YStack" data-sentry-source-file="FeedbackForm.tsx">
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="FeedbackForm.tsx">
          {!shouldShowMoreDetails ? initialForm : moreDetailsForm}
        </AnimatePresence>
      </YStack>

      <YStack paddingTop="$6" onLayout={handleButtonAreaLayout} data-sentry-element="YStack" data-sentry-source-file="FeedbackForm.tsx">
        <LoadingButton {...submitButtonProps} loading={loading} data-sentry-element="LoadingButton" data-sentry-source-file="FeedbackForm.tsx">
          {shouldShowMoreDetails ? moreDetailsProps?.submitText ?? "Submit" : continueButtonText}
        </LoadingButton>
      </YStack>
    </YStack>;
}